import { useCallback, useState, type ReactElement } from 'react';
import { Link, useOutletContext, useParams } from 'react-router';
import { useListePaginee } from '../coquille/useChargement.js';
import { CHEMINS } from '../routes.js';
import { armerAutomatisation } from '../automatisations.js';
import { EchecApi } from '../api.js';
import { etatLisible, LIBELLES_ETAT, CLASSES_ETAT,
         EXPLICATIONS_ETAT,
         type FaitsAutomatisation } from './etatAutomatisation.js';

interface Espace { readonly code: string; readonly nom: string; readonly role: string }

interface LigneAutomatisation {
  readonly id: string;
  readonly nom: string;
  readonly description: string | null;
  readonly est_arme: boolean;
  readonly a_version_active: boolean;
}

type EtatGeste =
  | { readonly etat: 'repos' }
  | { readonly etat: 'en_vol' }
  | { readonly etat: 'echoue'; readonly message: string };

interface ReponseListe {
  readonly automatisations: readonly LigneAutomatisation[];
  readonly curseur_suivant: string | null;
}

/**
 * LA LISTE DES AUTOMATISATIONS.
 *
 * ── CREER, PUIS OUVRIR [29-E] ────────
 *
 * La reserve de 29-C est LEVEE, pas contournee : creer sans editeur aurait
 * produit une coquille vide. L editeur existe, et le brouillon nait avec l
 * objet dans la meme transaction -- le CTA mene donc au formulaire, qui mene
 * a l editeur. La liste reste le lieu ou l on RETROUVE, jamais une etape
 * obligatoire de la creation.
 *
 * ── LES CINQ CHAMPS SONT CEUX DE L'API, PAS UN DE PLUS ────────
 *
 * La liste ne rend ni date de creation ni nombre de versions : c'est un INDEX,
 * pas un export. Afficher « derniere modification » supposerait de toucher une
 * route close ; la fiche le portera quand elle existera.
 */
export function Automatisations(): ReactElement {
  const espace = useOutletContext<Espace>();
  // Le projet vient du segment d URL [contrat 28-BJ] : la route est montee
  // sous /espaces/:code/projets/:projet/automatisations.
  const { projet } = useParams<{ projet: string }>();

  // `useCallback` : sans lui, une nouvelle fonction a chaque rendu relancerait
  // le chargement en boucle -- elle figure dans les dependances du hook.
  const extraire = useCallback((r: ReponseListe) => ({
    lignes: r.automatisations, curseur: r.curseur_suivant,
  }), []);

  const { etat, chargerSuite, recharger } =
    useListePaginee<ReponseListe, LigneAutomatisation>(
      `/locataires/${encodeURIComponent(espace.code)}`
      + `/projets/${encodeURIComponent(projet ?? '')}/automatisations`, extraire);

  return (
    <>
      <div className="contenu__entete">
        <div>
          <h1>Automatisations</h1>
          <p className="discret">Ce que votre espace fait tourner.</p>
        </div>
        <p><Link to={CHEMINS.nouvelleAutomatisation(espace.code, projet ?? '')}
          className="bouton bouton--discret">+ Nouvelle automatisation</Link></p>
      </div>

      {etat.phase === 'chargement' && <p className="discret">Chargement…</p>}

      {etat.phase === 'echec' && (
        <div className="carte">
          {/* LE CODE DE L'API DECIDE DU TON. Un refus de droit n'est pas une
              panne, et l'utilisateur doit savoir laquelle des deux il vit. */}
          <p className={etat.code === 'droit_insuffisant'
            ? 'message message--alerte' : 'message message--refus'} role="alert">
            {etat.message}
          </p>
          {etat.code !== 'droit_insuffisant' && (
            <button type="button" className="bouton"
                    style={{ marginTop: 'var(--espace-4)' }}
                    onClick={recharger}>Réessayer</button>
          )}
        </div>
      )}

      {etat.phase === 'pret' && etat.donnees.lignes.length === 0 && (
        <div className="carte etat-vide">
          <h3>Aucune automatisation</h3>
          <p className="discret">Cet espace n en contient pas encore.</p>
          <p><Link to={CHEMINS.nouvelleAutomatisation(espace.code, projet ?? '')}
            className="bouton">+ Creer la premiere automatisation</Link></p>
        </div>
      )}

      {etat.phase === 'pret' && etat.donnees.lignes.length > 0 && (
        <>
          <ul className="grille">
            {etat.donnees.lignes.map((a) => (
              <Carte key={a.id} automatisation={a}
                     code={espace.code} projet={projet ?? ''} />))}
          </ul>
          {/* « CHARGER LA SUITE », jamais un defilement infini : celui-ci rend
              le bas de page inatteignable et casse le retour arriere. */}
          {etat.donnees.curseurSuivant !== null && (
            <div style={{ marginTop: 'var(--espace-6)', textAlign: 'center' }}>
              <button type="button" className="bouton"
                      disabled={etat.donnees.chargeSuite}
                      onClick={chargerSuite}>
                {etat.donnees.chargeSuite ? 'Chargement…' : 'Charger la suite'}
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
}

function Carte({ automatisation, code, projet }: {
  automatisation: LigneAutomatisation; code: string; projet: string;
}): ReactElement {
  // L ETAT LOCAL PREND LE PAS SUR LA PROP, jamais l inverse. La ligne vient de
  // `useListePaginee` et n est pas mutable ; apres le geste on garde ICI les
  // deux faits RELUS par le serveur. Toucher le hook aurait remis en jeu ses
  // QUATRE consommateurs pour un besoin local [dette 79].
  const [apres, poserApres] = useState<FaitsAutomatisation | null>(null);
  const [geste, poserGeste] = useState<EtatGeste>({ etat: 'repos' });
  const faits = apres ?? automatisation;
  const e = etatLisible(faits);
  return (
    <li className="carte">
      {/*
        * 29-J -- LA CARTE N EST PLUS UN LIEN, ELLE EN CONTIENT UN.
        *
        * Le <Link> enveloppait TOUTE la carte. Un <button> a l interieur
        * serait du contenu interactif dans du contenu interactif : HTML
        * invalide, tsc vert, et un clic sur Armer aurait AUSSI navigue.
        * On ne pouvait donc pas ajouter le geste, seulement restructurer.
        *
        * L indentation le disait deja : les <p> et le <div> etaient a 6
        * espaces quand le <strong> etait a 8 -- le lien avait ete enroule
        * apres coup autour d un contenu qui ne lui appartenait pas.
        *
        * PORTEE VOLONTAIREMENT ETROITE [29-J]. Aucune classe ni motif
        * partage n est cree ici : cinq autres ecrans portent des cartes,
        * et le motif d actions secondaires reste a concevoir GLOBALEMENT
        * en 29-M. Ceci est une carte qui porte une action, pas un modele.
        */}
      <Link to={CHEMINS.editeur(code, projet, automatisation.id)}>
        <strong>{automatisation.nom}</strong>
      </Link>
      {automatisation.description !== null && automatisation.description.length > 0 ? (
        <p className="discret">{automatisation.description}</p>
      ) : (
        <p className="tres-discret">Sans description</p>
      )}
      <div style={{ marginTop: 'auto', paddingTop: 'var(--espace-2)' }}>
        <span className={CLASSES_ETAT[e]} title={EXPLICATIONS_ETAT[e]}>
          {LIBELLES_ETAT[e]}
        </span>
        {/*
          * 29-J -- DEUX LIBELLES, JAMAIS UN INTERRUPTEUR. Le moteur journalise
          * `automatisation.armee` et `automatisation.desarmee` separement, pour
          * qu on puisse demander « qui a desarme la facturation ? ». L ecran dit
          * donc ce qu il fait.
          *
          * `incoherent` NE RECOIT AUCUN BOUTON. Cet etat signale une anomalie du
          * moteur ; offrir « Desarmer » la transformerait silencieusement en
          * parcours normal et ferait disparaitre l indice a investiguer.
          *
          * `brouillon` RECOIT UN BOUTON DESACTIVE, jamais rien. Masquer le geste
          * n apprendrait pas qu il existe. Le moteur REFUSE ce cas depuis 29-J
          * (409 automatisation_sans_version_active) : l ecran ne fait qu annoncer
          * un refus reel, il n invente pas une regle.
          *
          * L EXPLICATION EST UN TEXTE RENDU, pas un `title` : celui-ci ne
          * s affiche pas au toucher et n est pas lu de facon fiable.
          */}
        {(e === 'en_pause' || e === 'en_service') && (
          <button type="button" className="bouton bouton--discret"
                  style={{ marginTop: 'var(--espace-2)' }}
                  disabled={geste.etat === 'en_vol'}
                  onClick={() => {
                    poserGeste({ etat: 'en_vol' });
                    void (async () => {
                      try {
                        const r = await armerAutomatisation(
                          code, projet, automatisation.id, e === 'en_pause');
                        // LE POINT D ADAPTATION. `etatLisible` veut un
                        // booleen ; le geste rend l objet ou null. On derive
                        // ICI, une seule fois, plutot que de demander au
                        // moteur un second champ qui dirait la meme chose.
                        poserApres({ est_arme: r.est_arme,
                          a_version_active: r.version_active !== null });
                        poserGeste({ etat: 'repos' });
                      } catch (err) {
                        // ON AFFICHE LE MESSAGE DU SERVEUR, on ne le retraduit
                        // pas. « Publiez une version en production avant
                        // d armer. » est deja redige pour l utilisateur ; en
                        // ecrire un second ici donnerait DEUX textes a tenir
                        // pour un seul refus. NouveauClient traduit parce que
                        // ses messages moteur sont techniques -- pas celui-ci.
                        poserGeste({ etat: 'echoue',
                          message: err instanceof EchecApi ? err.detail.message
                            : 'Le geste n a pas abouti.' });
                      }
                    })();
                  }}>
            {geste.etat === 'en_vol' ? 'Patientez…'
              : e === 'en_pause' ? 'Armer' : 'Désarmer'}
          </button>
        )}
        {e === 'brouillon' && (
          <>
            <button type="button" className="bouton bouton--discret"
                    style={{ marginTop: 'var(--espace-2)' }}
                    disabled aria-describedby={`pourquoi-${automatisation.id}`}>
              Armer
            </button>
            <p className="discret" id={`pourquoi-${automatisation.id}`}>
              Publiez une version avant d’armer.
            </p>
          </>
        )}
        {geste.etat === 'echoue' && (
          <p className="message message--refus" role="alert">{geste.message}</p>
        )}
      </div>
    </li>
  );
}
